import { useForm } from '@tanstack/react-form'
import { LakehouseConnectionTypeEnum, LakehouseTableInput, Uni } from '@vendia/management-api-types'
import { FetchLakehouseDataDocument } from '@vendia/control-plane-api'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import Button from 'src/components/buttons/button'
import Card from 'src/components/containers/card'
import Form from 'src/components/fields/form'
import MonacoEditorField from 'src/components/fields/monaco-editor.field'
import { ConfigurationCard } from 'src/components/lakehouse/tables/configuration-card'
import { RoleAccessCard } from 'src/components/lakehouse/tables/role-access-card'
import { SharingPoliciesCard } from 'src/components/lakehouse/tables/sharing-policies-card'
import { SyncStatusCard } from 'src/components/lakehouse/tables/sync-status-card'
import { TableDetailsCard } from 'src/components/lakehouse/tables/table-details-card'
import PageLoader from 'src/components/loaders/page-loader'
import PageHeader from 'src/components/page-header'
import { BasicTable } from 'src/components/tables/basic-table'
import { Table } from 'src/types/types'
import { isRequiredOnBlur } from 'src/utils/form/validation'
import useControlPlaneApi from 'src/utils/hooks/use-control-plane-api'
import { useGetUni } from 'src/utils/hooks/use-get-uni'
import { useGetVendiaTable } from 'src/utils/hooks/use-get-vendia-table'
import { getSourceTableInfo, SourceTableInfo } from 'src/utils/lakehouse/tables'

enum State {
  INITIALIZED,
  FETCHING_DATA,
  DATA_FETCH_SUCCESSFUL,
  DATA_FETCH_FAILED,
}

// Both SQL safe and lowercase (for glue database/table names)
function getSafeUniNodeName(uniName: string, nodeName: string) {
  return `${uniName}_${nodeName}`.replace(/-/g, '_').replace(/\./g, '_').toLowerCase()
}

export const PageLakehouseTable = () => {
  const { data: uniData } = useGetUni()
  const uni = uniData?.getUni as Uni | undefined
  const { table: tableId } = useParams<{ table: string; node: string }>()
  const { data: table } = useGetVendiaTable(tableId!)
  const sourceInfo = getSourceTableInfo(table ?? ({} as Table))

  if (!uni || !table) {
    return <PageLoader />
  }

  return <PageLakehouseTableContent uni={uni} table={table} sourceInfo={sourceInfo ?? ({} as SourceTableInfo)} />
}

const PageLakehouseTableContent = ({
  uni,
  table,
  sourceInfo,
}: {
  uni: Uni
  table: Table
  sourceInfo: SourceTableInfo
}) => {
  const { node: nodeName } = useParams<{ node: string }>()

  type LakehouseData = {
    [key: string]: any
  }
  const [data, setData] = useState<LakehouseData[]>([])
  const client = useControlPlaneApi()
  const [state, setState] = useState(State.INITIALIZED)
  const [expanded, setExpanded] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // Database name should match the _owner_ node of the table
  const databaseName = `${getSafeUniNodeName(uni.name, table._owner)}_db`

  const buildSqlQuery = (table: LakehouseTableInput) => {
    return `SELECT * FROM ${databaseName}.${table.name} limit 10;`
  }

  const form = useForm({
    defaultValues: {
      warehouse_sql_editor: buildSqlQuery(table),
    },
    onSubmit: async ({ value }: any) => {
      setState(State.FETCHING_DATA)

      const response = await client.graphqlFetch({ query: FetchLakehouseDataDocument, variables: {
        uniName: uni.name,
        nodeName: nodeName!,
        sqlText: value.warehouse_sql_editor?.trim(),
      } })
      if (response.errors?.length) {
        setErrorMessage(response.errors[0].message)
        setState(State.DATA_FETCH_FAILED)
        return
      }
      setData(response.fetchLakehouseData.results)
      setState(State.DATA_FETCH_SUCCESSFUL)
    },
  })

  const columnHeaders = Object.keys(data?.[0] ?? {})
  const rows =
    data?.map((row) => {
      // map over headers to ensure order and handle missing values
      return columnHeaders.map((header) => row[header])
    }) ?? []

  useEffect(() => {
    form.handleSubmit()
  }, [])

  const tableImage = (table: Table) => {
    const isOwnedTable = table._owner === nodeName
    return (
      <>
        {!isOwnedTable || sourceInfo.connectionType === LakehouseConnectionTypeEnum.Vendia ? (
          <img
            src='/images/vendor/vendia-primary.png'
            alt='Vendia logo'
            className='size-12 rounded-lg border border-slate-200'
          />
        ) : sourceInfo.connectionType === LakehouseConnectionTypeEnum.Snowflake ? (
          <img
            src='/images/vendor/snowflake-primary.png'
            alt='Snowflake logo'
            className='size-12 rounded-lg border border-slate-200'
          />
        ) : sourceInfo.connectionType === LakehouseConnectionTypeEnum.Cloudera ? (
          <img
            src='/images/vendor/cloudera-primary.png'
            alt='Cloudera logo'
            className='size-12 rounded-lg border border-slate-200'
          />
        ) : sourceInfo.connectionType === LakehouseConnectionTypeEnum.Postgres ? (
          <img
            src='/images/vendor/postgres-primary.png'
            alt='Postgres logo'
            className='size-12 rounded-lg border border-slate-200'
          />
        ) : sourceInfo.connectionType === LakehouseConnectionTypeEnum.Upload ? (
          <img
            src='/images/vendor/upload-primary.png'
            alt='File Upload logo'
            className='size-12 rounded-lg border border-slate-200'
          />
        ) : sourceInfo.connectionType === LakehouseConnectionTypeEnum.S3 ? (
          <img
            src='/images/vendor/s3-primary.png'
            alt='S3 logo'
            className='size-12 rounded-lg border border-slate-200'
          />
        ) : sourceInfo.connectionType === LakehouseConnectionTypeEnum.Mysql ? (
          <img
            src='/images/vendor/mysql-primary.png'
            alt='MySQL logo'
            className='size-12 rounded-lg border border-slate-200'
          />
        ) : sourceInfo.connectionType === LakehouseConnectionTypeEnum.Databricks ? (
          <img
            src='/images/vendor/databricks-primary.png'
            alt='Databricks logo'
            className='size-12 rounded-lg border border-slate-200'
          />
        ) : sourceInfo.connectionType === LakehouseConnectionTypeEnum.Bigquery ? (
          <img
            src='/images/vendor/bigquery-primary.png'
            alt='BigQuery logo'
            className='size-12 rounded-lg border border-slate-200'
          />
        ) : null}
      </>
    )
  }

  return (
    <div className='flex flex-grow flex-col'>
      <PageHeader
        title={`${table.name}`}
        // backLink='../../'
        // backText='Return to dashboard'
        testid='product-name-header'
        image={tableImage(table)}
      />
      <div className='grid grid-cols-[2fr,1fr] gap-6 p-6 px-12'>
        <div className='min-w-0'>
          <Card className='w-full' padding='none' overflow='visible'>
            <Form form={form}>
              <div>
                <div className='flex items-center justify-between p-6 text-sm font-bold'>
                  <h2>Query your data with SQL</h2>
                  <div className='flex gap-2'>
                    <Button kind='icon' size='small' icon='play' label='Run sql query' type='submit' />
                    <Button
                      kind='icon'
                      size='small'
                      icon={expanded ? 'chevron-up' : 'chevron-down'}
                      label='Toggle query details'
                      onClick={() => setExpanded(!expanded)}
                    />
                  </div>
                </div>
                {expanded && (
                  <div>
                    <MonacoEditorField
                      form={form}
                      language='sql'
                      name='warehouse_sql_editor'
                      validators={isRequiredOnBlur}
                      minHeight={150}
                      options={{
                        lineNumbers: 'on' as any,
                        padding: { top: 20 },
                      }}
                      theme='vs-dark'
                      className='overflow-hidden'
                      wrapperClassName='!mb-0'
                    />
                  </div>
                )}
              </div>
            </Form>
          </Card>
          <Card className='mt-6 w-full' padding='none'>
            <div className='flex flex-col gap-4'>
              {state === State.INITIALIZED && <div className='p-6'>Run a query to see results</div>}
              {state === State.FETCHING_DATA && (
                <div className='flex min-h-48 items-center justify-center text-center'>
                  <PageLoader />
                </div>
              )}
              {state === State.DATA_FETCH_SUCCESSFUL && (
                <div>
                  {rows.length === 0 ? (
                    <div className='border-uiBg-3 bg-uibg-0 flex min-h-48 items-center justify-center border'>
                      No data found
                    </div>
                  ) : (
                    <BasicTable columnHeaders={columnHeaders} rows={rows} data-testid='query-results-table' />
                  )}
                </div>
              )}
              {state === State.DATA_FETCH_FAILED && <div className='p-6'>{errorMessage}</div>}
            </div>
          </Card>
        </div>
        <div className='min-w-0 space-y-6'>
          <ConfigurationCard uni={uni} nodeName={nodeName!} table={table} sourceInfo={sourceInfo} />
          <SyncStatusCard uni={uni} nodeName={nodeName!} table={table} />
          {sourceInfo?.connectionType !== LakehouseConnectionTypeEnum.Upload &&
            sourceInfo.connectionType !== LakehouseConnectionTypeEnum.S3 && <SharingPoliciesCard table={table} />}
          <TableDetailsCard uni={uni} nodeName={nodeName!} tableKey={table._id} />
          <RoleAccessCard uni={uni} nodeName={nodeName!} table={table} />
        </div>
      </div>
    </div>
  )
}
